import { Injectable, signal, inject, OnDestroy, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError, Subject, connectable } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NotificationService } from './notification.service';
import { environment } from 'src/environments/environment';
import { User } from '../_helpers/user';
import { NavService } from 'src/app/theme/shared/service/navservice';

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
  private router = inject(Router);
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private ws!: WebSocket;

  private currentUserSignal = signal<User | null>(null);
  private loadingSignal = signal<boolean>(false);
  private notificationService = inject(NotificationService);
  /** Emits each incoming maintenance notification WS message. */
  private _notificationSubject = new Subject<any>();
  /** Observable that the NotificationService subscribes to for real-time updates. */
  public notification$ = this._notificationSubject.asObservable();

  private sessionInvalidHandled = false;
  isLogin = false;

  constructor(
    private toastr: ToastrService,
    private NavService: NavService
  ) {
    const stored = localStorage.getItem('currentUser');

    if (stored) {
      try {
        const user = JSON.parse(stored);
        this.currentUserSignal.set(user);

        if (user?.token) {
          this.isLogin = true;
          this.sessionInvalidHandled = false;

          this.connectWebSocket();

          // Wait until AuthenticationService has finished initialization.
          setTimeout(() => {
            if (this.isLogin && this.getToken()) {
              this.connectNotificationService();
            }
          }, 0);
        }
      } catch {
        localStorage.removeItem('currentUser');
      }
    }
  }

  handleSessionInvalid(): void {
    if (this.sessionInvalidHandled) return;
    this.sessionInvalidHandled = true;

    this.toastr.warning('Session expired or logged in elsewhere', 'Session Ended');
    this.Autologout();
  }

  private connectWebSocket(): void {
    const token = this.getToken();
    if (!token) return;
    if (!localStorage.getItem('browser_id')) {
      localStorage.setItem('browser_id', crypto.randomUUID());
    }
    const browserId = localStorage.getItem('browser_id');
    this.ws = new WebSocket(`${environment.wsUrl}/ws/user/?token=${token}&browser_id=${browserId}`);

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'FORCE_LOGOUT') {
          this.ngZone.run(() => this.handleSessionInvalid());
        } else if (data.type === 'NOTIFICATION') {
          // Real-time maintenance notification received via WebSocket.
          // Show a Toastr info toast and emit to subscribers (e.g. notification bell).
          this.ngZone.run(() => {
            this.toastr.info(data.message, 'Work Order Notification', {
              timeOut: 6000,
              progressBar: true,
              closeButton: true
            });
            this._notificationSubject.next(data);
          });
        }
      } catch {}
    };

    this.ws.onclose = () => {
      if (this.isLogin) {
        setTimeout(() => this.connectWebSocket(), 5000);
      }
    };

    this.ws.onerror = () => this.ws.close();
  }

  private disconnectWebSocket(): void {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = undefined!;
    }
  }

  fetchCurrentUser(): Observable<User> {
    const user = this.currentUserSignal();

    if (!user || !user.token) {
      this.Autologout();
      return throwError(() => new Error('No user'));
    }

    if (this.isTokenExpired(user.token)) {
      this.Autologout();
      return throwError(() => new Error('Token expired'));
    }

    return of(user);
  }

  login(identifier: string, password: string) {
    return this.http.post<any>(`${environment.apiUrl}/api/user/login/`, { identifier, password, device_type: 'web' }).pipe(
      tap((res: any) => {
        const user: User = {
          user: {
            id: res?.user?.id,
            username: res?.user?.username,
            email: res?.user?.email,
            logo: res?.user?.logo,
            favicon: res?.user?.favicon,
            role: res?.user?.role,
            is_active: res?.user?.is_active,
            company: res?.user?.company_id,
            menu_rights: res?.user?.menu_rights,
            menu_list: res?.user?.menu_list,
            home_page: res?.user?.home_page,
            style_order_no_create: res?.user?.style_order_no_create
          },
          token: res?.token,
          refresh_token: res?.refresh_token,
          user_id: res?.user?.id
        };

        this.currentUserSignal.set(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
        if (user.user.is_active) {
          this.isLogin = true;
        }
        // this.isLogin = true;
        this.sessionInvalidHandled = false;
        this.connectWebSocket();

        this.NavService.UpdateMenuList();
        // Connect to the separate real-time notification WebSocket.
        const accessToken = this.getToken();

        this.connectNotificationService();
        this.toastr.success('Login successful', 'Success');
      })
    );
  }

  private connectNotificationService(): void {
    const accessToken = this.getToken();

    if (!accessToken) {
      return;
    }

    // Open the separate notification WebSocket.
    this.notificationService.connect(accessToken);

    // Load existing notifications from the database.
    this.notificationService.loadNotifications().subscribe({
      error: (error) => {
        console.error('Failed to load notifications:', error);
      }
    });
  }

  // logout() {
  //   this.disconnectWebSocket();

  //   this.http.post(`${environment.apiUrl}/api/user/logout/`, {}).subscribe();

  //   localStorage.removeItem('currentUser');
  //   localStorage.removeItem('browser_id');
  //   localStorage.removeItem('selectedTenantId');
  //   this.currentUserSignal.set(null);
  //   this.isLogin = false;
  //   this.loadingSignal.set(false);

  //   this.router.navigate(['/login']);
  // }

  logout() {
    this.disconnectWebSocket();
    this.notificationService.disconnect();

    this.http.post(`${environment.apiUrl}/api/user/logout/`, {}).subscribe();

    localStorage.removeItem('currentUser');
    localStorage.removeItem('browser_id');
    localStorage.removeItem('selectedTenantId');

    this.currentUserSignal.set(null);
    this.isLogin = false;
    this.loadingSignal.set(false);

    this.router.navigate(['/login']);
  }

  // Autologout() {
  //   this.disconnectWebSocket();

  //   localStorage.removeItem('currentUser');
  //   this.currentUserSignal.set(null);
  //   this.isLogin = false;
  //   this.loadingSignal.set(false);

  //   this.router.navigate(['/login']);
  // }

  Autologout() {
    this.disconnectWebSocket();
    this.notificationService.disconnect();

    localStorage.removeItem('currentUser');
    this.currentUserSignal.set(null);
    this.isLogin = false;
    this.loadingSignal.set(false);

    this.router.navigate(['/login']);
  }

  register(id: string, email: string, password: string, firstName?: string, lastName?: string) {
    return this.http
      .post<any>(`${environment.apiUrl}/api/account/register`, {
        id,
        email,
        password,
        firstName,
        lastName
      })
      .pipe(
        switchMap(() => this.login(email, password)),
        catchError((err) => throwError(() => err))
      );
  }

  getToken(): string | null {
    return this.currentUserSignal()?.token || null;
  }

  isLoggedIn() {
    return this.isLogin;
  }

  get isLoading() {
    return this.loadingSignal();
  }

  get currentUserValue(): User | null {
    return this.currentUserSignal();
  }

  get currentUserName(): string | null {
    return this.currentUserSignal()?.user?.username || null;
  }

  get currentUserId(): string | null {
    return this.currentUserSignal()?.user?.id || null;
  }

  ngOnDestroy(): void {
    this.disconnectWebSocket();
    this.notificationService.disconnect();
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
}
